import { isEscapeKey } from '../helpers/escape-key-detection';
import { lockBody, unlockBody } from '../helpers/lockBody';
import { getWidthOfHidden, getWidthOfHiddenWithInvisibleParent } from '../helpers/getHeightOfHidden';
import stickyObserver from './stickyObserver';
import anchorNavigation from './anchorNavigation';
import A11yDialog from 'a11y-dialog';

// Static strings
const openMenuClass = 'nav-menu-is-open';
const navLoadedClass = 'nav-has-loaded';
const menuSlideStateAttribute = 'data-nav-expanded';
const navId = "navContainer";
const menuId = "navMenu";
const searchId = "navSearch";

class MainNavigation {
    container: HTMLElement;
    menuContainer: HTMLElement;
    menuTrigger: HTMLButtonElement;
    menuPanel: HTMLElement;
    menuFocusableChildren: Array<HTMLButtonElement | HTMLAnchorElement | HTMLInputElement>;
    menuIsOpen: boolean;
    navIsShowing: boolean;
    existingBodyStyles: string;
    lastScrollPosition: number;
    hasLoaded: boolean;
    currentNavHeight: number;
    bodyEl: HTMLBodyElement;
    onPageAnchorLinks: HTMLAnchorElement[];
    searchIsOpen: boolean;
    navItems: HTMLElement[] | any;
    searchTrigger: HTMLElement;
    searchPanel: HTMLElement;
    mainDrawer: HTMLElement;
    searchDrawer: HTMLElement;
    breadcrumbsHeight: number;
    viewportOffset: number;
    stickyNavEl: HTMLElement;
    stickyNavHeight: number;
    hasStickyNav: boolean;
    blockNavs: HTMLElement[];
    hasBlockNavs: boolean;

    constructor(navigation: HTMLElement) {
        this.container = navigation;
        this.menuTrigger = this.container.querySelector(`[aria-controls="${menuId}"]`) as HTMLButtonElement;
        this.menuPanel = document.querySelector(`#${menuId}`) as HTMLElement;
        this.searchPanel = document.querySelector(`#${searchId}`) as HTMLElement;
        this.menuFocusableChildren = [].slice.call(this.menuPanel.querySelectorAll('button, a, input'));
        this.bodyEl = document.body as HTMLBodyElement;
        this.menuIsOpen = false;
        this.navIsShowing = true;
        this.hasLoaded = false;
        this.existingBodyStyles = '';
        this.searchIsOpen = false;
        this.navItems = [].slice.call(this.menuPanel.querySelectorAll('[data-nav-item]'));
        this.searchTrigger = this.container.querySelector('[data-search-trigger]') as HTMLElement;
        this.breadcrumbsHeight = document.querySelector('[data-breadcrumbs]') ? (document.querySelector('[data-breadcrumbs]') as HTMLElement).offsetHeight : 0;
        this.stickyNavEl = document.querySelector('[data-sticky-nav]') as HTMLElement;
        this.hasStickyNav = this.stickyNavEl ? true : false;
        this.blockNavs = Array.from(document.querySelectorAll('[data-block-nav]'));
        this.hasBlockNavs = this.blockNavs.length > 0 ? true : false;
        this.stickyNavHeight = this.hasStickyNav ? (document.querySelector('[data-sticky-nav]') as HTMLElement).offsetHeight : 0;


        // Intercept on-page links so that the offset recalculates before the browser reaches it
        // This makes sure that the target is always comfortably visible below the nav
        this.onPageAnchorLinks = [].slice.call(document.querySelectorAll('a[href^="#"]'));
        this.onPageAnchorLinks.forEach(link => {
            link.addEventListener('click', (e: Event) => {
                e.preventDefault();
                const hash = link.getAttribute('href');
                const target = document.querySelector(hash) as HTMLElement;
                if (target) {
                    setTimeout(() => {
                        target.scrollIntoView(true);
                        target.focus();
                        window.location.hash = hash;
                    }, 0);
                }
            })
        })

    }

    init = () => {
        this.menuTrigger.addEventListener('click', () => {
            if (this.searchIsOpen) {
                this.closeSearch();
            }
            if (this.menuIsOpen) {
                this.closeMenu();
            }
            else {
                this.openMenu();
            }
        });

        this.searchTrigger.addEventListener('click', () => {

            if (this.menuIsOpen) {
                this.closeMenu();
                this.openSearch();
            }
            else if (this.searchIsOpen) {
                this.closeSearch();
            }
            else {
                this.openSearch();
            }
        });

        if (this.hasStickyNav) {
            this.initStickyNav();
        }

        this.navItems.forEach((panel: HTMLElement) => {

            panel.querySelector('.nav-menu-parent').addEventListener('click', () => {
                if (panel.classList.contains('open')) {
                    this.closeDrawers();
                }
                else {
                    this.closeDrawers();
                    this.openDrawer(panel);
                }
            })
        })

        document.body.addEventListener('keyup', (e: KeyboardEvent) => {
            if (e.which == 27 && this.menuIsOpen) { // Escape key
                this.closeMenu();
                this.menuTrigger.focus();
            }
            if (e.which == 9) { // Tab key. Show nav if tabbed into, or close menu if tabbed out of.
                const focusInNav = this.container.contains(document.activeElement);
                if (focusInNav && !this.navIsShowing) {
                }
                if (!focusInNav && this.menuIsOpen) {
                    this.closeMenu();
                }
            }
        });

        // Setup on page load
        this.closeMenu();
        this.setPageOffset();
        this.avoidContentFlash();
        this.initOutsideClickListener();
    }

 

    openMenu = () => {
        this.menuPanel.setAttribute('aria-hidden', 'false');
        this.menuTrigger.setAttribute('aria-expanded', 'true');
        this.enableTabbableChildren();
        this.menuIsOpen = true;
        this.existingBodyStyles = lockBody();
        this.menuPanel.classList.add(openMenuClass);
        this.closeOpenModals();
    }

    closeMenu = () => {
        this.menuPanel.setAttribute('aria-hidden', 'true');
        this.menuTrigger.setAttribute('aria-expanded', 'false');
        this.disableTabbableChildren();
        this.menuIsOpen = false;
        unlockBody(this.existingBodyStyles);
        this.menuPanel.classList.remove(openMenuClass);
        this.setPageOffset();
        this.closeDrawers();
    }

    openSearch = () => {
        this.searchIsOpen = true;
        this.existingBodyStyles = lockBody();
        this.searchPanel.setAttribute('aria-hidden', 'false');
        this.searchTrigger.setAttribute('aria-expanded', 'true');
        this.closeOpenModals();
    }

    closeSearch = () => {
        this.searchIsOpen = false;
        this.searchPanel.setAttribute('aria-hidden', 'true');
        unlockBody(this.existingBodyStyles);
        this.searchTrigger.setAttribute('aria-expanded', 'false');
    }

    // TODO: We may want/need to scope this to a specific attribute down the road
    closeOpenModals = () => {
        const modals = document.querySelectorAll('.dialog-container:not([aria-hidden="true"])');
        modals.forEach(modal => {
            const closeButton = modal.querySelector('.modal-close') as HTMLElement;
            if (closeButton) {
                closeButton.click();
            }
        });
    };

    openDrawer = (panel) => {
        panel.classList.add('open');
        panel.setAttribute('aria-expanded', 'true');
    }

    closeDrawers = () => {
        this.navItems.forEach((panel: HTMLElement) => {

            panel.classList.remove('open');
            panel.setAttribute('aria-expanded', 'false');
        })
    }
    enableTabbableChildren = () => {
        this.menuFocusableChildren.forEach(child => {
            child.tabIndex = 0;
        })
    }

    disableTabbableChildren = () => {
        this.menuFocusableChildren.forEach(child => {
            child.tabIndex = -1;
        })
    }

    setPageOffset = () => {
        let blockNavsTotalHeight = 0;
        if (this.hasBlockNavs && this.blockNavs.length > 0) {
            this.blockNavs.forEach((blockNav: HTMLElement) => {
                blockNavsTotalHeight += blockNav.offsetHeight;
            });
        }

        if (!this.hasLoaded) {
            this.container.classList.add(navLoadedClass);
            this.hasLoaded = true;
        }
        this.currentNavHeight = this.container.offsetHeight;
        this.viewportOffset = this.breadcrumbsHeight + this.stickyNavHeight + this.currentNavHeight + blockNavsTotalHeight;
        this.setCssVariables();
    }

    scrollHandler = () => {
        if (this.hasLoaded) {
            const currentPosition = window.pageYOffset;
            const delta = currentPosition - this.lastScrollPosition;
            this.lastScrollPosition = currentPosition;
            this.setPanelWidths();
        }
    }

    resizeHandler = () => {
        if (!this.menuIsOpen && !this.searchIsOpen) {
            this.avoidContentFlash();
        }
        this.setPageOffset();
        this.setPanelWidths();
    }

    setCssVariables = () => {
        document.documentElement.style.setProperty('--nav-clearance', `${this.navIsShowing ? this.currentNavHeight - 1 : 0}px`);
        document.documentElement.style.setProperty('--nav-margin-offset', `${this.navIsShowing ? this.currentNavHeight - 1 : 0}px`);
        document.documentElement.style.setProperty('--viewport-offset', `${this.viewportOffset}px`);
    }

    setPanelWidths = () => {
        this.menuPanel.style.setProperty('--menu-parent-width', `${this.menuPanel.offsetWidth}px`);
        this.searchPanel.style.setProperty('--menu-search-width', `${this.searchPanel.offsetWidth}px`);

        this.navItems.forEach((panel: HTMLElement) => {
            let secondaryDrawerWidth = (panel.querySelector('.nav-menu-child') as HTMLElement).offsetWidth;
            panel.style.setProperty('--menu-child-width', `${secondaryDrawerWidth}px`);
        });
    }

    // TODO: Fix this
    avoidContentFlash = () => {
        this.menuPanel.classList.add('menu-visually-hidden');
        this.searchPanel.classList.add('menu-visually-hidden');
        this.menuPanel.style.setProperty('--menu-parent-width', `${getWidthOfHidden(this.menuPanel)}px`);
        this.searchPanel.style.setProperty('--menu-search-width', `${getWidthOfHidden(this.searchPanel)}px`);
            this.navItems.forEach((panel: HTMLElement) => {
            let secondaryDrawerWidth = (panel.querySelector('.nav-menu-child') as HTMLElement);
                panel.style.setProperty('--menu-child-width', `${getWidthOfHiddenWithInvisibleParent(secondaryDrawerWidth, panel.parentElement)}px`);
        });
        setTimeout(() => {
            this.menuPanel.classList.remove('menu-visually-hidden');
            this.searchPanel.classList.remove('menu-visually-hidden');
        }, 1000)
    }

    initOutsideClickListener = () => {

        document.getElementById('mainContent').addEventListener('click', (event: MouseEvent) => {
            if (this.menuIsOpen) {
                this.closeMenu();
            }
            if (this.searchIsOpen) {
                this.closeSearch();
            }
        });
    }

    initStickyNav = () => {
        const observerElement = document.querySelector('[data-sticky-nav-observer]');
        new stickyObserver(this.stickyNavEl, observerElement)
        anchorNavigation(this.stickyNavEl);
    }

    get isTheMenuOpen() {
        return this.menuIsOpen;
    }
}


let mainNavigation: MainNavigation = null;
const navContainer = document.querySelector(`#${navId}`) as HTMLElement;
if (navContainer) {
    mainNavigation = new MainNavigation(navContainer);
    (window as any).mainNavigation = mainNavigation;
}

export { mainNavigation };




